import React, { useState } from 'react';
import { useCourseCreation } from '../../../contexts/CourseCreationContext';
import {
  Play,
  Clock,
  FileText,
  Save,
  Eye,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const VideoEditor = ({ content, onUpdate, onSave, onPreview }) => {
  const { validateContent } = useCourseCreation();
  const [errors, setErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  const handleFieldChange = (field, value) => {
    const updatedContent = { ...content, [field]: value };
    onUpdate(updatedContent);
    
    // Validation en temps réel
    const validation = validateContent(updatedContent, 'video');
    setErrors(validation.errors);
  };

  const handleSave = async () => {
    setIsValidating(true);
    const validation = validateContent(content, 'video');
    
    if (validation.valid) {
      await onSave(content);
      setErrors([]);
    } else {
      setErrors(validation.errors);
    }
    setIsValidating(false);
  };

  const getVideoThumbnail = (url) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
      return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
    }
    return null;
  };

  const isValidVideoUrl = (url) => {
    const patterns = [
      /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/,
      /^https?:\/\/(www\.)?vimeo\.com/,
      /\.(mp4|webm|ogg)$/i
    ];
    return patterns.some(pattern => pattern.test(url));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Play className="w-6 h-6 text-red-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Éditeur de Vidéo
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPreview(content)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <Eye className="w-4 h-4" />
            Aperçu
          </button>
          <button
            onClick={handleSave}
            disabled={isValidating || errors.length > 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {isValidating ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Erreurs */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Erreurs de validation
            </h3>
          </div>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulaire */}
        <div className="space-y-6">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Titre de la leçon *
            </label>
            <input
              type="text"
              value={content.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="Ex: Introduction au JavaScript"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              value={content.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Décrivez le contenu de cette leçon vidéo..."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
            />
          </div>

          {/* URL de la vidéo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              URL de la vidéo *
            </label>
            <div className="space-y-2">
              <input
                type="url"
                value={content.videoUrl || ''}
                onChange={(e) => handleFieldChange('videoUrl', e.target.value)}
                placeholder="https://www.youtube.com/watch?v=... ou https://vimeo.com/..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              {content.videoUrl && (
                <div className="flex items-center gap-2 text-sm">
                  {isValidVideoUrl(content.videoUrl) ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className={isValidVideoUrl(content.videoUrl) ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                    {isValidVideoUrl(content.videoUrl) ? 'URL valide' : 'URL invalide'}
                  </span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Supporte YouTube, Vimeo et fichiers MP4/WebM/OGG
            </p>
          </div>

          {/* Durée */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Durée estimée
            </label>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={content.duration || ''}
                onChange={(e) => handleFieldChange('duration', e.target.value)}
                placeholder="15:30"
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Format: MM:SS ou HH:MM:SS
            </p>
          </div>

          {/* Transcription */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Transcription
            </label>
            <textarea
              value={content.transcript || ''}
              onChange={(e) => handleFieldChange('transcript', e.target.value)}
              placeholder="Ajoutez la transcription de la vidéo pour l'accessibilité..."
              rows="6"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              La transcription améliore l'accessibilité et l'expérience utilisateur
            </p>
          </div>
        </div>

        {/* Aperçu */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Aperçu
          </h3>
          
          {content.videoUrl && isValidVideoUrl(content.videoUrl) ? (
            <div className="space-y-4">
              {/* Miniature vidéo */}
              <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                {getVideoThumbnail(content.videoUrl) ? (
                  <img
                    src={getVideoThumbnail(content.videoUrl)}
                    alt="Miniature vidéo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Informations de la vidéo */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  {content.title || 'Titre de la leçon'}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {content.description || 'Description de la leçon'}
                </p>
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  {content.duration && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{content.duration}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    <span>{content.transcript ? 'Transcription disponible' : 'Pas de transcription'}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Play className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400">
                  Aperçu vidéo non disponible
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoEditor;
